import React, { useState, useEffect, useMemo } from 'react'

import { motion, AnimatePresence } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import {
  Ship,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Scale,
  Search,
  Users,
  ArrowRight,
  RefreshCw,
  UserCheck,
  FileCheck,
  Eye,
  X,
  Layers,
  Plus,
  Check,
  Radio,
  Zap,
  GraduationCap,
  ChevronDown,
  Sparkles
} from 'lucide-react'
import api from '../../../services/authService'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import toast from 'react-hot-toast'
import { VisorExpedienteFisico, TipoDocumentoFisico } from './documentos/VisorExpedienteFisico'
import { MesaPrevioAduanero } from './componentes/MesaPrevioAduanero'
import { SimuladorDespachoEnVivo } from './componentes/SimuladorDespachoEnVivo'
import { ConfiguradorPracticasAulas } from './componentes/ConfiguradorPracticasAulas'
import { ModalModulacionDODA } from './componentes/ModalModulacionDODA'
import { IndicadorDiasLibres } from './componentes/IndicadorDiasLibres'
import { BarraCoordinadorSalones } from './componentes/BarraCoordinadorSalones'
import { ModalCrearSalon } from './componentes/ModalCrearSalon'
import { ModalAlumnosSalon } from './componentes/ModalAlumnosSalon'
import { ModalReasignarSalon } from './componentes/ModalReasignarSalon'
import { ModalReglasLigie } from '../../../componentes/modales/ModalReglasLigie'

// --- Tipos para Miembros y Grupos de la Institución ---
export interface MiembroInstitucion {
  id_miembro: number
  id_usuario: number
  rol_miembro: string
  estado: string
  nombre: string
  apellido: string
  avatar?: string
  email: string
  rol_sistema?: string
  turno?: string
  id_grupo?: number
}

export interface GrupoInstitucion {
  id_grupo: number
  nombre: string
  descripcion?: string
  miembros_count?: number
}

// --- Tipos de Datos del Despacho Portuario ---
export interface ExpedienteDespacho {
  id: string
  idSimulacion?: number
  idGrupo?: number
  folio: string
  titulo: string
  tipoOperacion: 'EXPORTACION' | 'IMPORTACION'
  regimen: 'A1_DEFINITIVO' | 'IR_RFE' | 'IN_IMMEX'
  estado:
    | 'BORRADOR'
    | 'PREAVISADO'
    | 'EN_TERMINAL'
    | 'ESPERANDO_MODULACION'
    | 'DESADUANADO'
    | 'EN_REVISION_RECEPTOR'
    | 'CERRADO'
    | 'INCIDENCIA'
  contenedor: {
    numero: string
    tipo: '20DC' | '40HC' | '40RF' | 'ISOTANQUE'
    lineaNaviera: string
    pesoBrutoKg: number
    taraKg: number
    vgmDeclaradoKg: number
    sellos: string
    servicioBP1Facturado: boolean
  }
  buque: {
    nombre: string
    viaje: string
    puertoOrigen: string
    puertoDestino: string
    terminal: string
    cutOffDocs: string
  }
  actores: {
    emisor: {
      nombre: string
      rfc: string
      pais: string
      alumnoId?: string | number
      alumnoNombre?: string
    }
    receptor: {
      nombre: string
      rfc: string
      pais: string
      domicilio: string
      alumnoId?: string | number
      alumnoNombre?: string
    }
    agenteAduanal: {
      nombre: string
      patente: string
      aduana: string
      alumnoId?: string | number
      alumnoNombre?: string
    }
    transportista: {
      razonSocial: string
      caat: string
      chofer: string
      placas: string
    }
  }
  documentos: {
    factura: {
      folio: string
      valorUSD: number
      incoterm: string
      fecha: string
    }
    packingList: {
      bultos: number
      pesoNetoKg: number
    }
    bl: {
      numero: string
      consignee: string
      shipper: string
    }
    anexo29: {
      folio: string
      fechaCaptura: string
      capturado: boolean
    }
    pasePIS: {
      codigoCita: string
      fechaCita: string
      iconoBascula: boolean
    }
  }
  incidencia?: {
    activa: boolean
    codigo: string
    descripcion: string
    solucion: string
  } | null
  tarjaInternacion: {
    firmadaPorReceptor: boolean
    fechaFirma?: string | null
    observacionesReceptor?: string
    resultado: 'PENDIENTE' | 'CONFORME' | 'DISCREPANCIA'
  }
  grupoNombre: string
  fechaCreacion: string
}

export const PaginaDespachoPortuario: React.FC = () => {
  const { usuario } = useAuth()

  // Determinar rol del usuario
  const esDocenteODirector =
    usuario?.tipo_organizacion === 'institucion' &&
    ['jefe', 'maestro', 'admin', 'supervisor'].includes(usuario?.rol_organizacion || '')

  // Estado principal de expedientes (limpio de mockups previos)
  const [expedientes, setExpedientes] = useState<ExpedienteDespacho[]>(() => {
    // Purgar de forma proactiva el almacenamiento previo que contenía datos ficticios
    localStorage.removeItem('logixport_expedientes_despacho')
    const guardados = localStorage.getItem('logixport_expedientes_reales_v1')
    if (guardados) {
      try {
        const parsed = JSON.parse(guardados)
        if (Array.isArray(parsed)) return parsed
      } catch (e) {
        return []
      }
    }
    return []
  })

  // Guardar en localStorage como cache resiliente
  useEffect(() => {
    localStorage.setItem('logixport_expedientes_reales_v1', JSON.stringify(expedientes))
  }, [expedientes])

  // Miembros y Grupos reales de la institución cargados desde el backend FastAPI
  const [miembros, setMiembros] = useState<MiembroInstitucion[]>([])
  const [grupos, setGrupos] = useState<GrupoInstitucion[]>([])
  const [cargandoBackend, setCargandoBackend] = useState<boolean>(false)

  // Cargar datos reales de la institución y simulaciones desde FastAPI
  const cargarDatosInstitucionYSimulaciones = async () => {
    setCargandoBackend(true)
    try {
      const [resMiembros, resDashboard, resSimulaciones, resGrupos] = await Promise.allSettled([
        api.get<MiembroInstitucion[]>('/api/institucion/miembros'),
        api.get('/api/institucion/dashboard/resumen'),
        api.get<any[]>('/api/simulaciones'),
        api.get<GrupoInstitucion[]>('/api/institucion/grupos')
      ])

      let miembrosObtenidos: MiembroInstitucion[] = []
      let gruposObtenidos: GrupoInstitucion[] = []

      if (resMiembros.status === 'fulfilled' && Array.isArray(resMiembros.value.data)) {
        miembrosObtenidos = resMiembros.value.data
      }

      if (resGrupos.status === 'fulfilled' && Array.isArray(resGrupos.value.data) && resGrupos.value.data.length > 0) {
        gruposObtenidos = resGrupos.value.data
      } else if (resDashboard.status === 'fulfilled' && resDashboard.value.data) {
        if (Array.isArray(resDashboard.value.data.grupos) && resDashboard.value.data.grupos.length > 0) {
          gruposObtenidos = resDashboard.value.data.grupos
        }
        if (miembrosObtenidos.length === 0 && Array.isArray(resDashboard.value.data.miembros)) {
          miembrosObtenidos = resDashboard.value.data.miembros
        }
      }

      if (miembrosObtenidos.length > 0) {
        setMiembros(miembrosObtenidos)
      }
      setGrupos(gruposObtenidos)

      // Si existe un único salón real registrado (ej. 4-CAS-1), enfocarlo automáticamente
      if (gruposObtenidos.length === 1) {
        setSalonSeleccionadoId(gruposObtenidos[0].id_grupo)
      }

      // Procesar simulaciones reales del backend
      if (resSimulaciones.status === 'fulfilled' && Array.isArray(resSimulaciones.value.data)) {
        const backendDespachos: ExpedienteDespacho[] = []
        for (const sim of resSimulaciones.value.data) {
          if (sim.tipo === 'DespachoPortuario' || sim.tipo === 'Despacho') {
            if (sim.datos_caso && typeof sim.datos_caso === 'object' && (sim.datos_caso.folio || sim.datos_caso.contenedor)) {
              const exp: ExpedienteDespacho = {
                ...sim.datos_caso,
                idSimulacion: sim.id_simulacion,
                titulo: sim.titulo || sim.datos_caso.titulo
              }
              backendDespachos.push(exp)
            }
          }
        }

        if (backendDespachos.length > 0) {
          setExpedientes((locales) => {
            const mapa = new Map<string, ExpedienteDespacho>()
            backendDespachos.forEach((b) => mapa.set(b.id, b))
            locales.forEach((l) => {
              if (!mapa.has(l.id)) {
                mapa.set(l.id, l)
              }
            })
            return Array.from(mapa.values())
          })
        }
      }
    } catch (error) {
      console.warn('Conexión con backend:', error)
    } finally {
      setCargandoBackend(false)
    }
  }

  useEffect(() => {
    cargarDatosInstitucionYSimulaciones()
  }, [])

  // Sincronizar actualización de expediente con el backend
  const sincronizarCambioEnBackend = async (expActualizado: ExpedienteDespacho) => {
    if (expActualizado.idSimulacion) {
      try {
        await api.put(`/api/simulaciones/${expActualizado.idSimulacion}`, {
          titulo: expActualizado.titulo,
          descripcion: `Despacho Portuario 1.0 - ${expActualizado.folio}`,
          tipo: 'DespachoPortuario',
          nivel_dificultad: 'intermedio',
          datos_caso: expActualizado
        })
      } catch (e) {
        console.warn('Error sincronizando cambio en backend:', e)
      }
    }
  }

  // Pestañas activas: 'cockpit' | 'simulador' | 'previo' | 'nuevo' | 'receptor' | 'asignador' | 'expediente_fisico'
  const [tabActiva, setTabActiva] = useState<'cockpit' | 'simulador' | 'previo' | 'nuevo' | 'receptor' | 'asignador' | 'expediente_fisico'>('cockpit')

  // Filtros de Salón y Operación
  const [salonSeleccionadoId, setSalonSeleccionadoId] = useState<number | 'TODOS'>('TODOS')
  const [vistaAgrupadaPorSalon, setVistaAgrupadaPorSalon] = useState<boolean>(true)
  const [modalCrearSalonAbierto, setModalCrearSalonAbierto] = useState<boolean>(false)
  const [mostrarModalReglasLigie, setMostrarModalReglasLigie] = useState(false)
  const [salonParaVerAlumnos, setSalonParaVerAlumnos] = useState<GrupoInstitucion | null>(null)
  const [expedienteParaReasignarSalon, setExpedienteParaReasignarSalon] = useState<ExpedienteDespacho | null>(null)
  const [grupoPreseleccionadoParaConfigurador, setGrupoPreseleccionadoParaConfigurador] = useState<number | undefined>(undefined)

  // Filtros de búsqueda y tipo
  const [filtroTipo, setFiltroTipo] = useState<'TODOS' | 'EXPORTACION' | 'IMPORTACION'>('TODOS')
  const [busqueda, setBusqueda] = useState('')
  const [expedienteSeleccionado, setExpedienteSeleccionado] = useState<ExpedienteDespacho | null>(null)

  // Expediente actualmente activo / enfocado (seguro contra nulos)
  const expedienteActual = expedienteSeleccionado || (expedientes.length > 0 ? expedientes[0] : null)

  // Estado para el modal de Cotejo del Receptor
  const [modalCotejoAbierto, setModalCotejoAbierto] = useState(false)
  const [observacionReceptor, setObservacionReceptor] = useState('')

  // Estado para el Visor de Hojas Físicas
  const [modalHojaFisicaAbierto, setModalHojaFisicaAbierto] = useState(false)
  const [docFisicoActivo, setDocFisicoActivo] = useState<TipoDocumentoFisico>('PEDIMENTO')

  // Estado para el Modal de Modulación DODA / Semáforo Fiscal ANAM
  const [modalModulacionDODAAbierto, setModalModulacionDODAAbierto] = useState(false)
  const [expedienteParaModular, setExpedienteParaModular] = useState<ExpedienteDespacho | null>(null)

  // Estado para el Wizard de Nuevo Despacho (Pasos 1 a 4)
  const [pasoWizard, setPasoWizard] = useState<1 | 2 | 3 | 4>(1)
  const [nuevoForm, setNuevoForm] = useState({
    titulo: '',
    tipoOperacion: 'EXPORTACION' as 'EXPORTACION' | 'IMPORTACION',
    regimen: 'A1_DEFINITIVO' as 'A1_DEFINITIVO' | 'IR_RFE' | 'IN_IMMEX',
    contenedorNumero: '',
    tipoContenedor: '40HC' as '20DC' | '40HC' | '40RF' | 'ISOTANQUE',
    lineaNaviera: 'MAERSK',
    buqueNombre: '',
    viaje: '',
    puertoOrigen: 'Manzanillo, México (MXZLO)',
    puertoDestino: '',
    emisorAlumnoId: undefined as number | undefined,
    emisorNombre: usuario?.nombre ? `${usuario.nombre} ${usuario.apellido || ''}` : 'Mi Empresa Exportadora S.A.',
    emisorRFC: 'MEX190412AA1',
    receptorNombre: '',
    receptorRFC: '',
    receptorDomicilio: '',
    alumnoReceptorId: undefined as number | undefined,
    alumnoReceptorNombre: 'Alumno Asignado',
    pesoMercanciaKg: 20000,
    taraKg: 3800,
    facturarBP1: true,
    transportistaNombre: 'Transportes Rápidos del Puerto S.A.',
    caat: '5T88',
    chofer: 'Pedro Martínez Gómez',
    placas: '22-TR-8A',
    sellos: 'SAT-992144',
    facturaFolio: 'FAC-2026-001',
    facturaValorUSD: 50000,
    incoterm: 'FOB Manzanillo',
    anexo29Folio: ''
  })

  // Filtrado de expedientes por Salón y por Búsqueda / Tipo
  const expedientesFiltrados = useMemo(() => {
    return expedientes.filter((exp) => {
      // Filtro de Salón
      if (salonSeleccionadoId !== 'TODOS') {
        const salonActivo = grupos.find((g) => g.id_grupo === salonSeleccionadoId)
        const matchId = exp.idGrupo === salonSeleccionadoId
        const matchNombre = salonActivo ? exp.grupoNombre === salonActivo.nombre : false
        if (!matchId && !matchNombre) return false
      }

      // Filtro de Tipo de Operación
      const matchTipo = filtroTipo === 'TODOS' || exp.tipoOperacion === filtroTipo

      // Filtro de Búsqueda
      const q = busqueda.toLowerCase()
      const matchBusqueda =
        exp.folio.toLowerCase().includes(q) ||
        exp.titulo.toLowerCase().includes(q) ||
        exp.contenedor.numero.toLowerCase().includes(q) ||
        exp.actores.receptor.nombre.toLowerCase().includes(q) ||
        exp.actores.emisor.nombre.toLowerCase().includes(q) ||
        (exp.grupoNombre && exp.grupoNombre.toLowerCase().includes(q))

      return matchTipo && matchBusqueda
    })
  }, [expedientes, salonSeleccionadoId, grupos, filtroTipo, busqueda])

  // Despachos donde el usuario es receptor (o pendientes de recepción)
  const despachosPendientesReceptor = useMemo(() => {
    return expedientesFiltrados.filter(
      (exp) => exp.estado === 'EN_REVISION_RECEPTOR' || !exp.tarjaInternacion.firmadaPorReceptor
    )
  }, [expedientesFiltrados])

  // Métricas rápidas contextuales al salón seleccionado o general
  const metricas = useMemo(() => {
    return {
      total: expedientesFiltrados.length,
      esperandoModulacion: expedientesFiltrados.filter((e) => e.estado === 'ESPERANDO_MODULACION').length,
      revisionReceptor: expedientesFiltrados.filter((e) => e.estado === 'EN_REVISION_RECEPTOR').length,
      desaduanados: expedientesFiltrados.filter((e) => e.estado === 'DESADUANADO' || e.estado === 'CERRADO').length,
      incidencias: expedientesFiltrados.filter((e) => e.estado === 'INCIDENCIA').length
    }
  }, [expedientesFiltrados])

  // Agrupación de expedientes por Salón para vista global
  const expedientesAgrupadosPorSalon = useMemo(() => {
    const mapa = new Map<string, ExpedienteDespacho[]>()

    // Asegurar que cada grupo registrado tenga su sección
    grupos.forEach((g) => {
      mapa.set(g.nombre, [])
    })

    // Repartir expedientes filtrados
    expedientesFiltrados.forEach((exp) => {
      const nombreGrupo = exp.grupoNombre || 'Comercio Exterior 7°A'
      if (!mapa.has(nombreGrupo)) {
        mapa.set(nombreGrupo, [])
      }
      mapa.get(nombreGrupo)!.push(exp)
    })

    return Array.from(mapa.entries()).map(([nombreGrupo, exps]) => {
      const grupoObj = grupos.find((g) => g.nombre === nombreGrupo)
      return {
        nombreGrupo,
        idGrupo: grupoObj?.id_grupo,
        descripcion: grupoObj?.descripcion || 'Salón Institucional',
        miembros_count: grupoObj?.miembros_count,
        expedientes: exps
      }
    })
  }, [expedientesFiltrados, grupos])

  // Reasignar expediente a otro salón
  const handleReasignarSalon = (expId: string, nuevoGrupoId: number, nuevoGrupoNombre: string) => {
    let expActualizado: ExpedienteDespacho | null = null
    setExpedientes((prev) =>
      prev.map((e) => {
        if (e.id === expId) {
          expActualizado = {
            ...e,
            idGrupo: nuevoGrupoId,
            grupoNombre: nuevoGrupoNombre
          }
          return expActualizado
        }
        return e
      })
    )
    if (expActualizado) {
      sincronizarCambioEnBackend(expActualizado)
      toast.success(`Expediente reasignado a "${nuevoGrupoNombre}"`)
    }
  }

  // Cuando se crea un nuevo salón
  const handleNuevoSalonCreado = (nuevoSalon: GrupoInstitucion) => {
    setGrupos((prev) => [...prev, nuevoSalon])
    setSalonSeleccionadoId(nuevoSalon.id_grupo)
  }

  // Función para crear un nuevo despacho desde el Wizard
  const handleCrearDespacho = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nuevoForm.titulo || !nuevoForm.contenedorNumero || !nuevoForm.receptorNombre) {
      toast.error('Por favor completa los campos principales del despacho')
      return
    }

    const vgmCalculado = nuevoForm.pesoMercanciaKg + nuevoForm.taraKg
    const nuevoExpediente: ExpedienteDespacho = {
      id: `DSP-2026-${String(expedientes.length + 1).padStart(3, '0')}`,
      folio: `EXP-ZLO-${Math.floor(10000 + Math.random() * 90000)}`,
      titulo: nuevoForm.titulo,
      tipoOperacion: nuevoForm.tipoOperacion,
      regimen: nuevoForm.regimen,
      estado: 'ESPERANDO_MODULACION',
      contenedor: {
        numero: nuevoForm.contenedorNumero.toUpperCase(),
        tipo: nuevoForm.tipoContenedor,
        lineaNaviera: nuevoForm.lineaNaviera,
        pesoBrutoKg: vgmCalculado,
        taraKg: nuevoForm.taraKg,
        vgmDeclaradoKg: vgmCalculado,
        sellos: nuevoForm.sellos,
        servicioBP1Facturado: nuevoForm.facturarBP1
      },
      buque: {
        nombre: nuevoForm.buqueNombre || 'MAERSK MC-KINNEY MOLLER',
        viaje: nuevoForm.viaje || '2604W',
        puertoOrigen: nuevoForm.puertoOrigen,
        puertoDestino: nuevoForm.puertoDestino || 'Long Beach, USA (USLGB)',
        terminal: nuevoForm.regimen === 'IR_RFE' ? 'SSA México RFE (700m Puerto)' : 'SSA México Manzanillo (TEC I)',
        cutOffDocs: '10/10/2026 18:00'
      },
      actores: {
        emisor: {
          nombre: nuevoForm.emisorNombre,
          rfc: nuevoForm.emisorRFC,
          pais: 'México',
          alumnoId: nuevoForm.emisorAlumnoId || (usuario?.id ? String(usuario.id) : undefined),
          alumnoNombre: `${usuario?.nombre || 'Alumno Actual'} (Emisor)`
        },
        receptor: {
          nombre: nuevoForm.receptorNombre,
          rfc: nuevoForm.receptorRFC,
          pais: nuevoForm.tipoOperacion === 'EXPORTACION' ? 'Internacional' : 'México',
          domicilio: nuevoForm.receptorDomicilio || 'Domicilio Fiscal del Receptor',
          alumnoId: nuevoForm.alumnoReceptorId ? String(nuevoForm.alumnoReceptorId) : undefined,
          alumnoNombre: nuevoForm.alumnoReceptorNombre
        },
        agenteAduanal: {
          nombre: 'Agencia Aduanal de Manzanillo S.C.',
          patente: '3450',
          aduana: 'Manzanillo (160)',
          alumnoNombre: 'Patente SSA 3450'
        },
        transportista: {
          razonSocial: nuevoForm.transportistaNombre,
          caat: nuevoForm.caat,
          chofer: nuevoForm.chofer,
          placas: nuevoForm.placas
        }
      },
      documentos: {
        factura: {
          folio: nuevoForm.facturaFolio,
          valorUSD: Number(nuevoForm.facturaValorUSD),
          incoterm: nuevoForm.incoterm,
          fecha: new Date().toLocaleDateString('es-MX')
        },
        packingList: {
          bultos: 1000,
          pesoNetoKg: nuevoForm.pesoMercanciaKg
        },
        bl: {
          numero: `BL-${nuevoForm.lineaNaviera}-${Math.floor(100000 + Math.random() * 900000)}`,
          consignee: nuevoForm.receptorNombre,
          shipper: nuevoForm.emisorNombre
        },
        anexo29: {
          folio: `ANX29-2026-${Math.floor(100000 + Math.random() * 900000)}`,
          fechaCaptura: new Date().toLocaleString('es-MX'),
          capturado: true
        },
        pasePIS: {
          codigoCita: `PIS-${Math.floor(1000000 + Math.random() * 9000000)}`,
          fechaCita: 'Cita Confirmada para Hoy',
          iconoBascula: nuevoForm.facturarBP1
        }
      },
      incidencia: null,
      tarjaInternacion: {
        firmadaPorReceptor: false,
        resultado: 'PENDIENTE'
      },
      grupoNombre: grupos[0]?.nombre || 'Comercio Exterior 7°A',
      fechaCreacion: new Date().toLocaleDateString('es-MX')
    }

    try {
      const res = await api.post('/api/simulaciones/crear', {
        titulo: nuevoExpediente.titulo,
        descripcion: `Despacho Portuario 1.0 - Folio ${nuevoExpediente.folio}`,
        tipo: 'DespachoPortuario',
        nivel_dificultad: 'intermedio',
        datos_caso: nuevoExpediente
      })
      if (res.data?.id) {
        nuevoExpediente.idSimulacion = res.data.id
      }
      toast.success('¡Expediente de Despacho guardado y sincronizado con el backend!')
    } catch (e) {
      console.warn('Backend offline o sin permisos, guardado en cache local:', e)
      toast.success('¡Expediente registrado en el simulador!')
    }

    setExpedientes((prev) => [nuevoExpediente, ...prev])
    setTabActiva('cockpit')
    setPasoWizard(1)
  }

  // Acción del Receptor: Aceptar Recepción
  const handleAceptarRecepcion = (expId: string) => {
    let expActualizado: ExpedienteDespacho | null = null
    setExpedientes((prev) =>
      prev.map((exp) => {
        if (exp.id === expId) {
          expActualizado = {
            ...exp,
            estado: 'CERRADO',
            tarjaInternacion: {
              firmadaPorReceptor: true,
              fechaFirma: new Date().toLocaleString('es-MX'),
              observacionesReceptor: observacionReceptor || 'Recepción conforme en muelle. Documentos y sellos íntegros.',
              resultado: 'CONFORME'
            }
          }
          return expActualizado
        }
        return exp
      })
    )
    if (expActualizado) sincronizarCambioEnBackend(expActualizado)
    setModalCotejoAbierto(false)
    setObservacionReceptor('')
    toast.success('¡Tarja de Internación firmada! Mercancía recibida e inventariada correctamente.')
  }

  // Acción del Receptor: Levantar Incidencia
  const handleLevantarIncidencia = (expId: string) => {
    let expActualizado: ExpedienteDespacho | null = null
    setExpedientes((prev) =>
      prev.map((exp) => {
        if (exp.id === expId) {
          expActualizado = {
            ...exp,
            estado: 'INCIDENCIA',
            incidencia: {
              activa: true,
              codigo: 'DISCREPANCIA_RECEPTOR',
              descripcion: observacionReceptor || 'Discrepancia detectada en cotejo de sellos o datos fiscales del consignatario.',
              solucion: 'El Agente Aduanal debe rectificar el documento de transporte y verificar el cotejo con el importador.'
            },
            tarjaInternacion: {
              firmadaPorReceptor: false,
              fechaFirma: null,
              observacionesReceptor: observacionReceptor,
              resultado: 'DISCREPANCIA'
            }
          }
          return expActualizado
        }
        return exp
      })
    )
    if (expActualizado) sincronizarCambioEnBackend(expActualizado)
    setModalCotejoAbierto(false)
    setObservacionReceptor('')
    toast.error('Acta de Discrepancia levantada. El despacho ha sido detenido para auditoría.')
  }

  // Abrir Modal de Modulación con DODA (Semáforo ANAM)
  const handleAbrirModulacionDODA = (exp: ExpedienteDespacho) => {
    setExpedienteParaModular(exp)
    setModalModulacionDODAAbierto(true)
  }

  // Concluir Modulación DODA y aplicar resultado ANAM
  const handleCompletarModulacionDODA = (
    expId: string,
    resultado: 'VERDE' | 'ROJO',
    detalleIncidencia?: {
      codigo: string
      descripcion: string
      solucion: string
    }
  ) => {
    let expActualizado: ExpedienteDespacho | null = null
    setExpedientes((prev) =>
      prev.map((exp) => {
        if (exp.id === expId) {
          expActualizado = {
            ...exp,
            estado: resultado === 'VERDE' ? 'EN_REVISION_RECEPTOR' : 'INCIDENCIA',
            incidencia:
              resultado === 'ROJO' && detalleIncidencia
                ? {
                    activa: true,
                    codigo: detalleIncidencia.codigo,
                    descripcion: detalleIncidencia.descripcion,
                    solucion: detalleIncidencia.solucion
                  }
                : null
          }
          return expActualizado
        }
        return exp
      })
    )
    if (expActualizado) {
      setExpedienteSeleccionado(expActualizado)
      sincronizarCambioEnBackend(expActualizado)
    }
  }

  // Resolver Incidencia Aduanera
  const handleResolverIncidencia = (expId: string) => {
    let expActualizado: ExpedienteDespacho | null = null
    setExpedientes((prev) =>
      prev.map((exp) => {
        if (exp.id === expId) {
          expActualizado = {
            ...exp,
            estado: 'EN_REVISION_RECEPTOR',
            incidencia: null
          }
          return expActualizado
        }
        return exp
      })
    )
    if (expActualizado) sincronizarCambioEnBackend(expActualizado)
    toast.success('Incidencia aduanera solventada. Procediendo a entrega al Receptor.')
  }

  // Renderizador de tarjeta individual de expediente
  const renderTarjetaExpediente = (exp: ExpedienteDespacho) => {
    const esExport = exp.tipoOperacion === 'EXPORTACION'
    const tieneIncidencia = exp.estado === 'INCIDENCIA'

    return (
      <div
        key={exp.id}
        className={`relative overflow-hidden rounded-2xl border p-5 sm:p-6 transition-all duration-300 hover:shadow-lg ${
          tieneIncidencia
            ? 'border-rose-500/40 bg-rose-500/[0.02] dark:bg-rose-500/[0.04]'
            : exp.estado === 'CERRADO'
            ? 'border-emerald-500/30 bg-emerald-500/[0.02] dark:bg-emerald-500/[0.04]'
            : 'border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0c0c0e]'
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Columna Izquierda: Identificación & Contenedor */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-black px-2.5 py-0.5 rounded-md bg-slate-900 text-white dark:bg-white dark:text-slate-950 font-mono">
                {exp.folio}
              </span>
              <span
                className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${
                  esExport
                    ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                    : 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20'
                }`}
              >
                {exp.tipoOperacion} · {exp.regimen}
              </span>

              {/* Badge de Estatus */}
              <span
                className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                  exp.estado === 'ESPERANDO_MODULACION'
                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                    : exp.estado === 'EN_REVISION_RECEPTOR'
                    ? 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/30'
                    : exp.estado === 'CERRADO'
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                    : 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30'
                }`}
              >
                {exp.estado === 'ESPERANDO_MODULACION' && 'Esperando Modulación'}
                {exp.estado === 'EN_REVISION_RECEPTOR' && 'En Revisión del Receptor'}
                {exp.estado === 'CERRADO' && 'Despacho y Tarja Cerrados'}
                {exp.estado === 'INCIDENCIA' && 'Incidencia / Alerta Activa'}
              </span>

              {/* Badge de Salón con botón para Reasignar */}
              <button
                type="button"
                onClick={() => setExpedienteParaReasignarSalon(exp)}
                className="px-2.5 py-0.5 rounded-md text-[10px] font-bold bg-purple-500/15 text-purple-600 dark:text-purple-300 border border-purple-500/30 hover:bg-purple-500/25 transition-all flex items-center gap-1 shadow-sm"
                title="Haz clic para reasignar este expediente a otro salón institucional"
              >
                <GraduationCap className="h-3 w-3 text-purple-400" />
                <span>Salón: {exp.grupoNombre || 'Comercio Exterior 7°A'}</span>
                <ChevronDown className="h-2.5 w-2.5 opacity-60 ml-0.5" />
              </button>
            </div>

            <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
              {exp.titulo}
            </h3>

            {/* Ficha Rápida del Contenedor */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 dark:text-zinc-400 font-medium">
              <span className="flex items-center gap-1.5 font-mono font-bold text-slate-900 dark:text-white">
                <Layers className="h-4 w-4 text-cyan-500" />
                {exp.contenedor.numero} ({exp.contenedor.tipo})
              </span>
              <span>Naviera: <strong>{exp.contenedor.lineaNaviera}</strong></span>
              <span className="flex items-center gap-1">
                <Scale className="h-3.5 w-3.5 text-blue-500" />
                VGM: <strong>{(exp.contenedor.vgmDeclaradoKg / 1000).toFixed(1)} Ton</strong>
              </span>
              <span className="flex items-center gap-1">
                <Ship className="h-3.5 w-3.5 text-indigo-500" />
                Buque: <strong>{exp.buque.nombre}</strong>
              </span>
            </div>

            {/* Indicador de Días Libres de Almacenaje y Demoras Navieras */}
            <IndicadorDiasLibres expediente={exp} compacto={true} />
          </div>

          {/* Columna Central: Actores Asignados (Emisor vs Receptor) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/60 dark:border-white/5 text-xs">
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Emisor / Shipper
              </p>
              <p className="font-bold text-slate-900 dark:text-white truncate max-w-[200px]">
                {exp.actores.emisor.nombre}
              </p>
              <p className="text-[10px] text-cyan-600 dark:text-cyan-400 font-medium">
                {exp.actores.emisor.alumnoNombre || 'Alumno Asignado'}
              </p>
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Receptor / Consignatario
              </p>
              <p className="font-bold text-slate-900 dark:text-white truncate max-w-[200px]">
                {exp.actores.receptor.nombre}
              </p>
              <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">
                {exp.actores.receptor.alumnoNombre || 'Alumno Asignado'}
              </p>
            </div>
          </div>

          {/* Columna Derecha: Acciones Operativas */}
          <div className="flex flex-wrap lg:flex-col items-end justify-center gap-2 shrink-0">
            {/* Botón Ver Detalle */}
            <button
              onClick={() => setExpedienteSeleccionado(exp)}
              className="px-3.5 py-2 rounded-xl border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5"
            >
              <Eye className="h-3.5 w-3.5" />
              <span>Ver Dossier</span>
            </button>

            {/* Botón Ver Hoja Física */}
            <button
              onClick={() => {
                setExpedienteSeleccionado(exp)
                setDocFisicoActivo('PEDIMENTO')
                setModalHojaFisicaAbierto(true)
              }}
              className="px-3.5 py-2 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs font-bold hover:bg-amber-500/20 transition-all flex items-center gap-1.5"
            >
              <FileText className="h-3.5 w-3.5 text-amber-500" />
              <span>Hoja Física</span>
            </button>

            {/* Botón Examen Previo */}
            <button
              onClick={() => {
                setExpedienteSeleccionado(exp)
                setTabActiva('previo')
              }}
              className="px-3.5 py-2 rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs font-bold hover:bg-blue-500/20 transition-all flex items-center gap-1.5"
            >
              <Search className="h-3.5 w-3.5 text-blue-500" />
              <span>Hacer Previo</span>
            </button>

            {/* Botón Simular Despacho Portuario */}
            <button
              onClick={() => {
                setExpedienteSeleccionado(exp)
                setTabActiva('simulador')
              }}
              className="px-3.5 py-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 text-xs font-bold hover:bg-cyan-500/20 transition-all flex items-center gap-1.5"
            >
              <Zap className="h-3.5 w-3.5 text-cyan-500" />
              <span>Simular en Vivo</span>
            </button>

            {/* Acción de Modulación con DODA */}
            {exp.estado === 'ESPERANDO_MODULACION' && (
              <button
                onClick={() => handleAbrirModulacionDODA(exp)}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 text-xs font-black uppercase tracking-wider shadow-md shadow-emerald-500/20 transition-all flex items-center gap-1.5"
              >
                <Radio className="h-3.5 w-3.5" />
                <span>Modular con DODA</span>
              </button>
            )}

            {/* Acción de Revisión del Receptor */}
            {exp.estado === 'EN_REVISION_RECEPTOR' && (
              <button
                onClick={() => {
                  setExpedienteSeleccionado(exp)
                  setModalCotejoAbierto(true)
                }}
                className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-black uppercase tracking-wider shadow-md shadow-purple-500/20 transition-all flex items-center gap-1.5"
              >
                <UserCheck className="h-3.5 w-3.5" />
                <span>Cotejar y Tarjar</span>
              </button>
            )}

            {/* Acción de Resolver Incidencia */}
            {exp.estado === 'INCIDENCIA' && (
              <button
                onClick={() => handleResolverIncidencia(exp.id)}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-wider shadow-md shadow-rose-500/20 transition-all flex items-center gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Solventar Alerta</span>
              </button>
            )}
          </div>
        </div>

        {/* Banner de Incidencia si existe */}
        {tieneIncidencia && exp.incidencia && (
          <div className="mt-4 p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/25 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="flex items-center gap-2">
                <span className="font-black text-rose-700 dark:text-rose-400">
                  {exp.incidencia.codigo}
                </span>
                <span className="text-rose-600/90 dark:text-rose-300">
                  {exp.incidencia.descripcion}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-600 dark:text-zinc-400">
                <strong>Acción requerida:</strong> {exp.incidencia.solucion}
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 h-full space-y-6 pb-12 min-h-screen bg-slate-50 text-slate-900 dark:bg-transparent dark:text-zinc-100 -m-4 lg:-m-8 p-4 lg:p-8">
      {/* ========================================================================= */}
      {/* HEADER INTERACTIVO GLASSMÓRFICO PREMIUM (ESTILO ALMACÉN DE PRÁCTICA) */}
      {/* ========================================================================= */}
      <div className="border-b border-slate-200/50 dark:border-white/[0.05] pb-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-500 bg-clip-text text-transparent dark:from-white dark:to-zinc-500 tracking-tight flex items-center gap-2.5 sm:gap-3">
              <div className="p-2 sm:p-2.5 rounded-2xl bg-cyan-100/50 dark:bg-white/[0.03] border border-transparent dark:border-white/[0.05] shadow-inner shrink-0">
                <Ship className="h-6 w-6 sm:h-7 sm:w-7 text-cyan-600 dark:text-zinc-300" />
              </div>
              <span className="truncate">Despacho y Puerto</span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-2 text-xs sm:text-sm font-medium text-slate-500 dark:text-zinc-400">
              <span>SSA Manzanillo TEC I · Despacho y Aduanas 1.0</span>
              <span className="text-slate-300 dark:text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1.5 font-bold text-emerald-600 dark:text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {miembros.length} Alumnos Reales
              </span>
              {grupos.length > 0 && (
                <span className="inline-flex items-center gap-1 font-bold text-indigo-600 dark:text-indigo-400">
                  · {grupos.length} Salón{grupos.length !== 1 ? 'es' : ''} ({grupos.map((g) => g.nombre).join(', ')})
                </span>
              )}
            </div>
          </div>

          {/* Acciones Principales y Estado Backend */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <button
              type="button"
              onClick={cargarDatosInstitucionYSimulaciones}
              disabled={cargandoBackend}
              title="Recargar usuarios reales y datos desde el backend FastAPI"
              className="p-2.5 rounded-xl border border-slate-200/50 bg-white/50 dark:border-white/10 dark:bg-white/[0.02] text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
            >
              <RefreshCw className={`h-4 w-4 ${cargandoBackend ? 'animate-spin text-cyan-500' : ''}`} />
            </button>

            {esDocenteODirector && (
              <button
                type="button"
                onClick={() => setModalCrearSalonAbierto(true)}
                className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-slate-200/50 bg-white/50 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-slate-700 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300 hover:bg-white dark:border-white/10 dark:bg-white/[0.02] dark:text-zinc-300 dark:hover:bg-white/[0.05]"
                title="Crear un nuevo salón institucional o aula"
              >
                <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:scale-110 text-slate-500 dark:text-zinc-400" />
                <span>+ Salón</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setMostrarModalReglasLigie(true)}
              title="Consultar Artículo 2 LIGIE: Las 6 Reglas Generales y Complementarias"
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 sm:px-4 py-2.5 text-xs sm:text-sm font-bold text-amber-700 dark:text-amber-300 shadow-xs backdrop-blur-sm transition-all hover:bg-amber-500/20 hover:border-amber-500/50 cursor-pointer"
            >
              <Scale className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 transition-transform group-hover:scale-110 shrink-0" />
              <span className="whitespace-nowrap">Reglas LIGIE (Art. 2)</span>
            </button>

            <button
              onClick={() => setTabActiva('nuevo')}
              className="flex-1 sm:flex-none justify-center group relative flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-bold text-white dark:text-black shadow-lg shadow-blue-500/25 dark:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02]"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:rotate-90 shrink-0" />
              <span className="whitespace-nowrap">Nuevo Despacho</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* NAVEGACIÓN POR PESTAÑAS (PILLS) SEGMENTADAS (ESTILO ALMACÉN DE PRÁCTICA) */}
      {/* ========================================================================= */}
      <div className="flex overflow-x-auto gap-1 bg-slate-100/80 dark:bg-white/[0.03] p-1.5 rounded-2xl sm:rounded-full w-full sm:w-fit border border-slate-200/50 dark:border-white/[0.05] shadow-sm mb-6 no-scrollbar touch-pan-x">
        <button
          onClick={() => setTabActiva('cockpit')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            tabActiva === 'cockpit'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <Ship className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Expedientes</span>
          {expedientes.length > 0 && (
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] font-black bg-blue-500/10 text-blue-600 dark:text-blue-300 border border-blue-500/20">
              {expedientes.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setTabActiva('simulador')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            tabActiva === 'simulador'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Simulador 1.0</span>
          <span className="ml-1 px-1.5 py-0.2 rounded-full text-[9px] font-black bg-cyan-500/10 text-cyan-600 dark:text-cyan-300 border border-cyan-500/20">
            En Vivo
          </span>
        </button>

        <button
          onClick={() => {
            setDocFisicoActivo('PEDIMENTO')
            setTabActiva('expediente_fisico')
          }}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            tabActiva === 'expediente_fisico'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Hojas Físicas</span>
        </button>

        <button
          onClick={() => setTabActiva('previo')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            tabActiva === 'previo'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <Search className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Mesa Previo</span>
        </button>

        <button
          onClick={() => setTabActiva('receptor')}
          className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
            tabActiva === 'receptor'
              ? 'bg-white dark:bg-white/10 text-purple-600 dark:text-purple-300 shadow-md border border-slate-200/50 dark:border-transparent'
              : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
          }`}
        >
          <UserCheck className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Receptor</span>
          {despachosPendientesReceptor.length > 0 && (
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-purple-500 text-white font-black animate-pulse">
              {despachosPendientesReceptor.length}
            </span>
          )}
        </button>

        {esDocenteODirector && (
          <button
            onClick={() => setTabActiva('asignador')}
            className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold rounded-xl sm:rounded-full transition-all whitespace-nowrap shrink-0 ${
              tabActiva === 'asignador'
                ? 'bg-white dark:bg-white/10 text-indigo-600 dark:text-white shadow-md border border-slate-200/50 dark:border-transparent'
                : 'text-slate-600 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
            }`}
          >
            <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Prácticas Aula</span>
          </button>
        )}
      </div>

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA 1: COCKPIT DE EXPEDIENTES Y COORDINADOR DE SALONES */}
      {/* ========================================================================= */}
      {tabActiva === 'cockpit' && (
        <div className="space-y-6">
          {/* BARRA COORDINADORA DE SALONES INSTITUCIONALES */}
          <BarraCoordinadorSalones
            grupos={grupos}
            salonActivoId={salonSeleccionadoId}
            onSeleccionarSalon={(salonId) => setSalonSeleccionadoId(salonId)}
            expedientes={expedientes}
            totalAlumnos={miembros.length}
            onAbrirCrearSalon={() => setModalCrearSalonAbierto(true)}
            onAbrirConfiguradorConSalon={(salonId) => {
              setGrupoPreseleccionadoParaConfigurador(salonId)
              setTabActiva('asignador')
            }}
            onAbrirVerAlumnosSalon={(salon) => setSalonParaVerAlumnos(salon)}
            vistaAgrupada={vistaAgrupadaPorSalon}
            onToggleVistaAgrupada={() => setVistaAgrupadaPorSalon(!vistaAgrupadaPorSalon)}
          />

          {/* TARJETAS DE MÉTRICAS OPERATIVAS (BENTO CARDS ESTILO ALMACÉN DE PRÁCTICA) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
            <div className="p-4 sm:p-5 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm hover:border-blue-500/30 transition-all">
              <div className="flex items-center justify-between text-slate-500 dark:text-zinc-400">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">Total</span>
                <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500">
                  <Ship className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{metricas.total}</p>
              <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500">Operaciones en ciclo</span>
            </div>

            <div className="p-4 sm:p-5 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm hover:border-amber-500/30 transition-all">
              <div className="flex items-center justify-between text-slate-500 dark:text-zinc-400">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">Modulación</span>
                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500">
                  <Clock className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold text-amber-500 tracking-tight">{metricas.esperandoModulacion}</p>
              <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500">Esperando semáforo</span>
            </div>

            <div className="p-4 sm:p-5 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm hover:border-purple-500/30 transition-all">
              <div className="flex items-center justify-between text-slate-500 dark:text-zinc-400">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">Receptor</span>
                <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-500">
                  <UserCheck className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold text-purple-500 tracking-tight">{metricas.revisionReceptor}</p>
              <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500">Cotejo / Tarja</span>
            </div>

            <div className="p-4 sm:p-5 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm hover:border-emerald-500/30 transition-all">
              <div className="flex items-center justify-between text-slate-500 dark:text-zinc-400">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">Desaduanados</span>
                <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold text-emerald-500 tracking-tight">{metricas.desaduanados}</p>
              <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500">Concluidos</span>
            </div>

            <div className="p-4 sm:p-5 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm hover:border-rose-500/30 transition-all col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between text-slate-500 dark:text-zinc-400">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">Incidencias</span>
                <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-500">
                  <AlertTriangle className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-2 text-2xl sm:text-3xl font-extrabold text-rose-500 tracking-tight">{metricas.incidencias}</p>
              <span className="text-[10px] font-medium text-slate-400 dark:text-zinc-500">Discrepancias</span>
            </div>
          </div>

          {/* Barra de Filtro y Búsqueda Glassmórfica */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-[22px] border border-slate-200/80 bg-white/80 dark:border-white/[0.08] dark:bg-[#0c0c0c] backdrop-blur-xl shadow-sm">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por contenedor, folio, buque o receptor..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-xs bg-slate-100/80 dark:bg-white/[0.04] border border-slate-200/60 dark:border-white/10 focus:border-cyan-500 focus:outline-none dark:text-white"
              />
            </div>

            <div className="flex items-center gap-1.5 self-start sm:self-auto bg-slate-100/70 dark:bg-white/[0.03] p-1 rounded-xl border border-slate-200/50 dark:border-white/[0.05]">
              {(['TODOS', 'EXPORTACION', 'IMPORTACION'] as const).map((tipo) => (
                <button
                  key={tipo}
                  onClick={() => setFiltroTipo(tipo)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    filtroTipo === tipo
                      ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm border border-slate-200/50 dark:border-transparent'
                      : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {tipo}
                </button>
              ))}
            </div>
          </div>

          {/* Listado de Tarjetas de Expedientes: AGRUPADA POR SALÓN o FILTRADA */}
          {salonSeleccionadoId === 'TODOS' && vistaAgrupadaPorSalon ? (
            <div className="space-y-8">
              {expedientesAgrupadosPorSalon.map((salonData) => (
                <div
                  key={salonData.nombreGrupo}
                  className="rounded-[24px] border border-slate-200/80 dark:border-white/[0.08] bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl p-5 sm:p-6 space-y-4 shadow-xl"
                >
                  {/* Encabezado del Salón */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200/60 dark:border-white/[0.06]">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                        <GraduationCap className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
                            {salonData.nombreGrupo}
                          </h2>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20">
                            {salonData.expedientes.length} expedientes
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                          {salonData.descripcion} {salonData.miembros_count !== undefined ? `· ${salonData.miembros_count} Alumnos matriculados` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {salonData.idGrupo && (
                        <button
                          type="button"
                          onClick={() => {
                            const grp = grupos.find((g) => g.id_grupo === salonData.idGrupo)
                            if (grp) setSalonParaVerAlumnos(grp)
                          }}
                          className="px-3.5 py-1.5 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] text-xs font-bold text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5 shadow-sm"
                        >
                          <Users className="h-3.5 w-3.5 text-indigo-500" />
                          <span>Ver Alumnos</span>
                        </button>
                      )}

                      {salonData.idGrupo && (
                        <button
                          type="button"
                          onClick={() => {
                            setGrupoPreseleccionadoParaConfigurador(salonData.idGrupo)
                            setTabActiva('asignador')
                          }}
                          className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 text-white dark:text-black font-extrabold text-xs shadow-md shadow-blue-500/20 dark:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02] flex items-center gap-1.5"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          <span>+ Práctica a este Salón</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          if (salonData.idGrupo) {
                            setSalonSeleccionadoId(salonData.idGrupo)
                          }
                        }}
                        className="px-3.5 py-1.5 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] text-slate-700 dark:text-zinc-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5"
                      >
                        <span>Filtrar este Salón</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>

                  {/* Listado de Expedientes de este salón */}
                  {salonData.expedientes.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                      {salonData.expedientes.map(renderTarjetaExpediente)}
                    </div>
                  ) : (
                    <div className="py-10 px-6 text-center border border-slate-200/60 dark:border-white/[0.06] bg-slate-50/50 dark:bg-white/[0.01] rounded-2xl">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-500 mx-auto mb-3">
                        <GraduationCap className="h-6 w-6" />
                      </div>
                      <h4 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Salón {salonData.nombreGrupo} listo para operaciones
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-md mx-auto">
                        Aún no hay expedientes asignados. Puedes crear una práctica interactiva para distribuir casos reales entre los alumnos de este salón.
                      </p>
                      {salonData.idGrupo && (
                        <div className="flex items-center justify-center gap-3 mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setGrupoPreseleccionadoParaConfigurador(salonData.idGrupo)
                              setTabActiva('asignador')
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 text-white dark:text-black font-extrabold text-xs shadow-md shadow-blue-500/20 dark:shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02]"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            <span>Generar Práctica para {salonData.nombreGrupo}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {expedientesFiltrados.length > 0 ? (
                expedientesFiltrados.map(renderTarjetaExpediente)
              ) : (
                <div className="bg-white/80 dark:bg-[#0c0c0c] backdrop-blur-xl rounded-[24px] p-8 sm:p-12 text-center border border-slate-200/80 dark:border-white/[0.08] shadow-xl">
                  <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-500 mx-auto mb-4">
                    <Ship className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
                    {salonSeleccionadoId !== 'TODOS'
                      ? `Salón ${grupos.find(g => g.id_grupo === salonSeleccionadoId)?.nombre || 'Seleccionado'} listo para Despacho`
                      : 'Sin expedientes registrados'}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 mt-1 max-w-lg mx-auto">
                    {salonSeleccionadoId !== 'TODOS'
                      ? 'No hay operaciones asignadas actualmente a este salón. Puedes generar prácticas automáticas con roles o dar de alta un despacho manual.'
                      : 'No se encontraron expedientes con los filtros aplicados. Prueba cambiando el salón seleccionado o generando una práctica.'}
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof salonSeleccionadoId === 'number') {
                          setGrupoPreseleccionadoParaConfigurador(salonSeleccionadoId)
                        }
                        setTabActiva('asignador')
                      }}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-white dark:to-gray-200 text-white dark:text-black font-extrabold text-xs sm:text-sm shadow-lg shadow-blue-500/20 dark:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all hover:scale-[1.02] flex items-center gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>Generar Práctica con Células</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTabActiva('nuevo')}
                      className="px-4 py-2.5 rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] text-slate-700 dark:text-zinc-300 font-bold text-xs sm:text-sm hover:bg-slate-100 dark:hover:bg-white/5 transition-all flex items-center gap-1.5"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Nuevo Despacho Manual</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA 2: BANDEJA DEL RECEPTOR (COTEJO Y TARJA) */}
      {/* ========================================================================= */}
      {tabActiva === 'receptor' && (
        <div className="space-y-6">
          <div className="p-6 rounded-3xl bg-gradient-to-r from-purple-900/20 via-indigo-900/10 to-transparent border border-purple-500/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-purple-600 text-white shadow-lg shadow-purple-500/30">
                <UserCheck className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white">
                  Mesa de Trabajo del Receptor / Consignatario
                </h2>
                <p className="mt-1 text-xs text-slate-600 dark:text-zinc-400 max-w-2xl leading-relaxed">
                  En este módulo, el alumno asignado con el rol de <strong>Receptor (Importador / Consignatario)</strong> audita los embarques entrantes. Es tu responsabilidad realizar el <strong>Cross-Check Documental</strong> (Factura vs B/L vs Tarja física en terminal) y firmar la Tarja de Internación o levantar una Discrepancia antes del retiro del contenedor.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {despachosPendientesReceptor.map((exp) => (
              <div
                key={exp.id}
                className="p-6 rounded-2xl border border-purple-500/25 bg-white dark:bg-[#0c0c0e] shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black px-2.5 py-0.5 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                      {exp.folio}
                    </span>
                    <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">
                      Terminal: {exp.buque.terminal}
                    </span>
                  </div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white">
                    {exp.titulo}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 dark:text-zinc-400">
                    <span>Receptor Declarado: <strong>{exp.actores.receptor.nombre}</strong></span>
                    <span>RFC/Tax ID: <strong>{exp.actores.receptor.rfc}</strong></span>
                    <span>Contenedor: <strong>{exp.contenedor.numero}</strong></span>
                    <span>Bultos: <strong>{exp.documentos.packingList.bultos}</strong></span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setExpedienteSeleccionado(exp)
                      setModalCotejoAbierto(true)
                    }}
                    className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-purple-500/25 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                  >
                    <FileCheck className="h-4 w-4" />
                    <span>Realizar Cotejo & Tarja</span>
                  </button>
                </div>
              </div>
            ))}

            {despachosPendientesReceptor.length === 0 && (
              <div className="text-center py-16 border border-dashed border-slate-300 dark:border-white/10 rounded-2xl">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                <h4 className="text-base font-bold text-slate-800 dark:text-white">
                  Bandeja de recepción al día
                </h4>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">
                  Todos los despachos asignados a receptores han sido revisados y firmados.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA 3: ASISTENTE DE NUEVO DESPACHO (WIZARD) */}
      {/* ========================================================================= */}
      {tabActiva === 'nuevo' && (
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Barra de Pasos */}
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-white/10 pb-4">
            {[
              { num: 1, label: 'Booking & Buque' },
              { num: 2, label: 'Emisor & Receptor' },
              { num: 3, label: 'Anexo 29 & Transportista' },
              { num: 4, label: 'VGM & Cita SSA' }
            ].map((step) => (
              <button
                key={step.num}
                onClick={() => setPasoWizard(step.num as any)}
                className={`flex items-center gap-2 text-xs font-bold transition-colors ${
                  pasoWizard === step.num
                    ? 'text-blue-600 dark:text-cyan-400'
                    : pasoWizard > step.num
                    ? 'text-emerald-500'
                    : 'text-slate-400 dark:text-zinc-600'
                }`}
              >
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center font-black ${
                    pasoWizard === step.num
                      ? 'bg-blue-600 text-white dark:bg-cyan-500 dark:text-slate-950'
                      : pasoWizard > step.num
                      ? 'bg-emerald-500/20 text-emerald-500'
                      : 'bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-zinc-400'
                  }`}
                >
                  {pasoWizard > step.num ? <Check className="h-4 w-4" /> : step.num}
                </div>
                <span className="hidden sm:inline">{step.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleCrearDespacho} className="space-y-6">
            {/* PASO 1: BOOKING & BUQUE */}
            {pasoWizard === 1 && (
              <div className="space-y-5 rounded-2xl border border-slate-200/80 bg-white/80 dark:border-white/10 dark:bg-[#0c0c0e] p-6 shadow-sm">
                <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <Ship className="h-5 w-5 text-blue-500" />
                  Paso 1 · Reservación de Espacio y Buque (Forecast TOS)
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Título o Referencia del Embarque *
                    </label>
                    <input
                      type="text"
                      required
                      value={nuevoForm.titulo}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, titulo: e.target.value })}
                      placeholder="Ej. Exportación FCL Aguacate Hass a Rotterdam"
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-cyan-500 focus:outline-none dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Tipo de Operación
                    </label>
                    <select
                      value={nuevoForm.tipoOperacion}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, tipoOperacion: e.target.value as any })}
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    >
                      <option value="EXPORTACION">Exportación (Salida a Puerto)</option>
                      <option value="IMPORTACION">Importación (Ingreso a Territorio)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Régimen Aduanero
                    </label>
                    <select
                      value={nuevoForm.regimen}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, regimen: e.target.value as any })}
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    >
                      <option value="A1_DEFINITIVO">A1 · Importación / Exportación Definitiva</option>
                      <option value="IR_RFE">IR · Recinto Fiscalizado Estratégico (RFE Manzanillo)</option>
                      <option value="IN_IMMEX">IN · Importación Temporal IMMEX</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Número de Contenedor (ISO 6346) *
                    </label>
                    <input
                      type="text"
                      required
                      value={nuevoForm.contenedorNumero}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, contenedorNumero: e.target.value })}
                      placeholder="Ej. MSCU7821940"
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs font-mono uppercase bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Tipo y Medida del Contenedor
                    </label>
                    <select
                      value={nuevoForm.tipoContenedor}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, tipoContenedor: e.target.value as any })}
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    >
                      <option value="40HC">40 High Cube (Dry)</option>
                      <option value="20DC">20 Standard Dry</option>
                      <option value="40RF">40 Reefer (Refrigerado)</option>
                      <option value="ISOTANQUE">Isotanque Grado Químico / Alimentos</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Línea Naviera
                    </label>
                    <select
                      value={nuevoForm.lineaNaviera}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, lineaNaviera: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    >
                      <option value="MAERSK">MAERSK (MSK)</option>
                      <option value="MSC">MEDITERRANEAN SHIPPING CO (MSC)</option>
                      <option value="HAPAG-LLOYD">HAPAG-LLOYD (HLC)</option>
                      <option value="CMA-CGM">CMA CGM (CMD)</option>
                      <option value="COSCO">COSCO CONTAINER LINE (COS)</option>
                      <option value="ONE">OCEAN NETWORK EXPRESS (ONE)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Buque y Viaje
                    </label>
                    <input
                      type="text"
                      value={nuevoForm.buqueNombre}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, buqueNombre: e.target.value })}
                      placeholder="Ej. MSC PALOMA - Viaje 2604W"
                      className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:outline-none dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={() => setPasoWizard(2)}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                  >
                    <span>Siguiente: Actores del Despacho</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* PASO 2: ASIGNACIÓN DE EMISOR Y RECEPTOR */}
            {pasoWizard === 2 && (
              <div className="space-y-5 rounded-2xl border border-slate-200/80 bg-white/80 dark:border-white/10 dark:bg-[#0c0c0e] p-6 shadow-sm">
                <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  Paso 2 · Asignación de Roles (Emisor & Receptor)
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Bloque Emisor */}
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 space-y-3">
                    <p className="text-xs font-black uppercase tracking-wider text-cyan-600 dark:text-cyan-400">
                      Datos del Emisor / Shipper
                    </p>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400 mb-1">
                        Razón Social Emisor *
                      </label>
                      <input
                        type="text"
                        required
                        value={nuevoForm.emisorNombre}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, emisorNombre: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400 mb-1">
                        RFC del Exportador *
                      </label>
                      <input
                        type="text"
                        required
                        value={nuevoForm.emisorRFC}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, emisorRFC: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 rounded-lg text-xs font-mono uppercase bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Bloque Receptor */}
                  <div className="p-4 rounded-xl border border-purple-500/30 bg-purple-500/[0.02] space-y-3">
                    <p className="text-xs font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
                      Datos del Receptor / Consignatario
                    </p>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400 mb-1">
                        Razón Social del Receptor *
                      </label>
                      <input
                        type="text"
                        required
                        value={nuevoForm.receptorNombre}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, receptorNombre: e.target.value })}
                        placeholder="Ej. Rotterdam Fresh Logistics B.V."
                        className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400 mb-1">
                        RFC / Tax ID del Receptor *
                      </label>
                      <input
                        type="text"
                        required
                        value={nuevoForm.receptorRFC}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, receptorRFC: e.target.value.toUpperCase() })}
                        placeholder="Ej. NL884912048B01 o RFC nacional"
                        className="w-full px-3 py-2 rounded-lg text-xs font-mono uppercase bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-zinc-400 mb-1">
                        Alumno Asignado como Receptor (Usuario Real de la Institución)
                      </label>
                      {miembros.length > 0 && (
                        <select
                          value={nuevoForm.alumnoReceptorId || ''}
                          onChange={(e) => {
                            const id = Number(e.target.value)
                            const m = miembros.find((x) => x.id_usuario === id)
                            setNuevoForm({
                              ...nuevoForm,
                              alumnoReceptorId: id || undefined,
                              alumnoReceptorNombre: m ? `${m.nombre} ${m.apellido}` : nuevoForm.alumnoReceptorNombre,
                              receptorNombre: nuevoForm.receptorNombre || (m ? `${m.nombre} ${m.apellido} Import S.A.` : '')
                            })
                          }}
                          className="w-full mb-1.5 px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                        >
                          <option value="">-- Seleccionar de los {miembros.length} usuarios de la institución --</option>
                          {miembros.map((m) => (
                            <option key={m.id_usuario} value={m.id_usuario}>
                              {m.nombre} {m.apellido} · {m.rol_miembro.toUpperCase()} ({m.email})
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        type="text"
                        value={nuevoForm.alumnoReceptorNombre}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, alumnoReceptorNombre: e.target.value })}
                        placeholder="Nombre o alias del alumno receptor"
                        className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setPasoWizard(1)}
                    className="px-5 py-2 rounded-xl border border-slate-200 dark:border-white/10 text-xs font-bold"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasoWizard(3)}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                  >
                    <span>Siguiente: Anexo 29 & Transportista</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* PASO 3: ANEXO 29 & TRANSPORTISTA */}
            {pasoWizard === 3 && (
              <div className="space-y-5 rounded-2xl border border-slate-200/80 bg-white/80 dark:border-white/10 dark:bg-[#0c0c0e] p-6 shadow-sm">
                <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <FileText className="h-5 w-5 text-emerald-500" />
                  Paso 3 · Anexo 29 de las RGCE & Datos del Transportista
                </h3>

                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  Requisito oficial exigido por la Aduana y SSA Marine para el ingreso de unidades al recinto fiscalizado de Manzanillo.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Línea Transportista
                    </label>
                    <input
                      type="text"
                      value={nuevoForm.transportistaNombre}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, transportistaNombre: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Código CAAT *
                    </label>
                    <input
                      type="text"
                      required
                      value={nuevoForm.caat}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, caat: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 rounded-lg text-xs font-mono uppercase bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Nombre del Chofer *
                    </label>
                    <input
                      type="text"
                      required
                      value={nuevoForm.chofer}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, chofer: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Placas del Tractocamión *
                    </label>
                    <input
                      type="text"
                      required
                      value={nuevoForm.placas}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, placas: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 rounded-lg text-xs font-mono uppercase bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Sellos Fiscales del Contenedor (Separados con //)
                    </label>
                    <input
                      type="text"
                      value={nuevoForm.sellos}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, sellos: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg text-xs font-mono bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setPasoWizard(2)}
                    className="px-5 py-2 rounded-xl border border-slate-200 dark:border-white/10 text-xs font-bold"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasoWizard(4)}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                  >
                    <span>Siguiente: Pesaje VGM & Cita SSA</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* PASO 4: PESAJE VGM & CITA SSA */}
            {pasoWizard === 4 && (
              <div className="space-y-5 rounded-2xl border border-slate-200/80 bg-white/80 dark:border-white/10 dark:bg-[#0c0c0e] p-6 shadow-sm">
                <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <Scale className="h-5 w-5 text-cyan-500" />
                  Paso 4 · Masa Bruta Verificada (VGM / SOLAS) & Cita PIS
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Peso Neto de la Mercancía (Kg)
                    </label>
                    <input
                      type="number"
                      value={nuevoForm.pesoMercanciaKg}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, pesoMercanciaKg: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                      Tara del Contenedor (Kg)
                    </label>
                    <input
                      type="number"
                      value={nuevoForm.taraKg}
                      onChange={(e) => setNuevoForm({ ...nuevoForm, taraKg: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div className="sm:col-span-2 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-black text-cyan-700 dark:text-cyan-300">
                        VGM Estimado Total: {(nuevoForm.pesoMercanciaKg + nuevoForm.taraKg).toLocaleString()} Kg (
                        {((nuevoForm.pesoMercanciaKg + nuevoForm.taraKg) / 1000).toFixed(2)} Toneladas)
                      </p>
                      <p className="text-[11px] text-slate-600 dark:text-zinc-400 mt-0.5">
                        Cumple con el límite de Max Gross (32.5 Ton). Tolerancia máxima en báscula: ±500 Kg.
                      </p>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nuevoForm.facturarBP1}
                        onChange={(e) => setNuevoForm({ ...nuevoForm, facturarBP1: e.target.checked })}
                        className="rounded text-cyan-600 h-4 w-4"
                      />
                      <span className="text-xs font-bold text-slate-800 dark:text-white">Facturar BP1 (Báscula)</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setPasoWizard(3)}
                    className="px-5 py-2 rounded-xl border border-slate-200 dark:border-white/10 text-xs font-bold"
                  >
                    Anterior
                  </button>

                  <button
                    type="submit"
                    className="px-7 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/25 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Emitir y Preavisar Despacho</span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA 4: CONFIGURADOR PEDAGÓGICO DE CÉLULAS Y PRÁCTICAS */}
      {/* ========================================================================= */}
      {tabActiva === 'asignador' && esDocenteODirector && (
        <ConfiguradorPracticasAulas
          grupos={grupos}
          miembros={miembros}
          grupoPreseleccionadoId={grupoPreseleccionadoParaConfigurador}
          onPublicarCasos={async (nuevosCasos) => {
            setExpedientes((prev) => [...nuevosCasos, ...prev])
            setTabActiva('cockpit')
          }}
          onVolver={() => setTabActiva('cockpit')}
        />
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA: SIMULADOR DE DESPACHO Y PUERTO 1.0 (EN VIVO) */}
      {/* ========================================================================= */}
      {tabActiva === 'simulador' && (
        <SimuladorDespachoEnVivo
          expediente={expedienteActual}
          todosExpedientes={expedientes}
          onSeleccionarExpediente={(exp) => setExpedienteSeleccionado(exp)}
          onAbrirHojaFisica={(tipoDoc) => {
            setDocFisicoActivo(tipoDoc)
            setModalHojaFisicaAbierto(true)
          }}
          onActualizarExpediente={(expActualizado) => {
            setExpedientes((prev) => prev.map((e) => (e.id === expActualizado.id ? expActualizado : e)))
            setExpedienteSeleccionado(expActualizado)
            sincronizarCambioEnBackend(expActualizado)
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA: MESA DEL PREVIO ADUANERO (PIZARRA OPERATIVA) */}
      {/* ========================================================================= */}
      {tabActiva === 'previo' && (
        <MesaPrevioAduanero
          expediente={expedienteActual}
          todosExpedientes={expedientes}
          onSeleccionarExpediente={(exp) => setExpedienteSeleccionado(exp)}
          onAbrirHojaFisica={(tipoDoc) => {
            setDocFisicoActivo(tipoDoc)
            setModalHojaFisicaAbierto(true)
          }}
          onActualizarExpediente={(expActualizado) => {
            setExpedientes((prev) => prev.map((e) => (e.id === expActualizado.id ? expActualizado : e)))
            setExpedienteSeleccionado(expActualizado)
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* CONTENIDO DE PESTAÑA 5: EXPEDIENTE EN HOJAS FÍSICAS */}
      {/* ========================================================================= */}
      {tabActiva === 'expediente_fisico' && (
        <VisorExpedienteFisico
          expediente={expedienteActual}
          todosExpedientes={expedientes}
          onSeleccionarExpediente={(exp) => setExpedienteSeleccionado(exp)}
          documentoInicial={docFisicoActivo}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: COTEJO DOCUMENTAL Y FIRMA DEL RECEPTOR */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {modalCotejoAbierto && expedienteSeleccionado && (
          <motion.div
            key="modal-cotejo-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setModalCotejoAbierto(false)
            }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm w-screen h-screen"
          >
            <motion.div
              key="modal-cotejo-dialog"
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-[#0e0e12]"
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  <h3 className="text-xl font-black text-slate-900 dark:text-white">
                    Mesa de Cotejo Documental (Cross-Check)
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setModalCotejoAbierto(false)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 sm:p-8 overflow-y-auto space-y-6">
                {/* Matriz de Comparación Cruzada */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Tarjeta 1: Factura Comercial */}
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] space-y-2">
                    <p className="text-[10px] font-black uppercase text-blue-600 dark:text-blue-400">
                      1. Factura Comercial
                    </p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                      Folio: {expedienteSeleccionado?.documentos?.factura?.folio || 'FAC-S/N'}
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Receptor: <strong>{expedienteSeleccionado?.actores?.receptor?.nombre || 'No asignado'}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      RFC/TaxID: <strong>{expedienteSeleccionado?.actores?.receptor?.rfc || 'RFC-PENDIENTE'}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Valor: <strong>${Number(expedienteSeleccionado?.documentos?.factura?.valorUSD || 0).toLocaleString()} USD</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setDocFisicoActivo('FACTURA')
                        setModalHojaFisicaAbierto(true)
                      }}
                      className="mt-2 w-full py-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300 text-[10px] font-bold hover:bg-blue-500/20 transition-all flex items-center justify-center gap-1.5"
                    >
                      <FileText className="h-3 w-3" />
                      <span>Auditar Hoja de Factura Física</span>
                    </button>
                  </div>

                  {/* Tarjeta 2: Bill of Lading (B/L) */}
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] space-y-2">
                    <p className="text-[10px] font-black uppercase text-cyan-600 dark:text-cyan-400">
                      2. Bill of Lading (B/L)
                    </p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                      B/L: {expedienteSeleccionado?.documentos?.bl?.numero || 'BL-PENDIENTE'}
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Consignee: <strong>{expedienteSeleccionado?.documentos?.bl?.consignee || expedienteSeleccionado?.actores?.receptor?.nombre || 'No asignado'}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Contenedor: <strong>{expedienteSeleccionado?.contenedor?.numero || 'S/N'}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Sellos: <strong>{expedienteSeleccionado?.contenedor?.sellos || 'Sin sellos'}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setDocFisicoActivo('BL')
                        setModalHojaFisicaAbierto(true)
                      }}
                      className="mt-2 w-full py-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 text-[10px] font-bold hover:bg-cyan-500/20 transition-all flex items-center justify-center gap-1.5"
                    >
                      <Ship className="h-3 w-3" />
                      <span>Auditar B/L Marítimo Físico</span>
                    </button>
                  </div>

                  {/* Tarjeta 3: Tarja Física Terminal SSA */}
                  <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] space-y-2">
                    <p className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400">
                      3. Tarja de Terminal SSA
                    </p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                      Terminal: {expedienteSeleccionado?.buque?.terminal || 'SSA Manzanillo'}
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Bultos en Muelle: <strong>{expedienteSeleccionado?.documentos?.packingList?.bultos ?? 0}</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Peso Báscula: <strong>{((expedienteSeleccionado?.contenedor?.pesoBrutoKg || 0) / 1000).toFixed(1)} Ton</strong>
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-zinc-400">
                      Pase PIS: <strong>{expedienteSeleccionado?.documentos?.pasePIS?.codigoCita || 'S/N'}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setDocFisicoActivo('TARJA')
                        setModalHojaFisicaAbierto(true)
                      }}
                      className="mt-2 w-full py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold hover:bg-emerald-500/20 transition-all flex items-center justify-center gap-1.5"
                    >
                      <Layers className="h-3 w-3" />
                      <span>Auditar Tarja SSA y Báscula</span>
                    </button>
                  </div>
                </div>

                {/* Área de Comentarios del Receptor */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                    Dictamen u Observaciones del Receptor al Firmar la Tarja
                  </label>
                  <textarea
                    rows={3}
                    value={observacionReceptor}
                    onChange={(e) => setObservacionReceptor(e.target.value)}
                    placeholder="Ej. Mercancía cotejada en muelle conforme a factura y lista de empaque. Sin discrepancias en sellos fiscales ni faltantes."
                    className="w-full px-3.5 py-2.5 rounded-xl text-xs bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 dark:text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                {/* Botones de Decisión */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-2">
                  <button
                    type="button"
                    onClick={() => handleLevantarIncidencia(expedienteSeleccionado.id)}
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-rose-500/40 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 font-bold text-xs uppercase tracking-wider transition-all"
                  >
                    Levantar Acta de Discrepancia
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAceptarRecepcion(expedienteSeleccionado.id)}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-purple-500/25 transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Firmar y Aceptar Tarja</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========================================================================= */}
      {/* MODAL 2: DETALLE DE EXPEDIENTE / DOSSIER COMPLETO */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {expedienteSeleccionado && !modalCotejoAbierto && !modalModulacionDODAAbierto && (
          <motion.div
            key="modal-dossier-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setExpedienteSeleccionado(null)
            }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm w-screen h-screen"
          >
            <motion.div
              key="modal-dossier-dialog"
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-[#0e0e12]"
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.02]">
                <div>
                  <span className="text-xs font-black text-cyan-600 dark:text-cyan-400">
                    {expedienteSeleccionado?.folio}
                  </span>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                    {expedienteSeleccionado?.titulo}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setExpedienteSeleccionado(null)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 sm:p-8 overflow-y-auto space-y-6 text-xs">
                {/* Ficha Técnica del Dossier */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Contenedor</p>
                    <p className="font-mono font-bold text-slate-900 dark:text-white text-sm">
                      {expedienteSeleccionado?.contenedor?.numero || 'S/N'}
                    </p>
                    <p className="text-slate-500">{expedienteSeleccionado?.contenedor?.tipo || '40HC'}</p>
                  </div>

                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Naviera</p>
                    <p className="font-bold text-slate-900 dark:text-white text-sm truncate">
                      {expedienteSeleccionado?.contenedor?.lineaNaviera || 'Naviera'}
                    </p>
                    <p className="text-slate-500 truncate">{expedienteSeleccionado?.buque?.nombre || 'Buque'}</p>
                  </div>

                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Peso VGM</p>
                    <p className="font-bold text-slate-900 dark:text-white text-sm">
                      {((expedienteSeleccionado?.contenedor?.vgmDeclaradoKg || 0) / 1000).toFixed(1)} Ton
                    </p>
                    <p className="text-emerald-500 font-medium">Báscula BP1 OK</p>
                  </div>

                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Terminal</p>
                    <p className="font-bold text-slate-900 dark:text-white text-sm">
                      {expedienteSeleccionado?.buque?.terminal || 'SSA Manzanillo'}
                    </p>
                    <p className="text-slate-500">Muelle 10-11</p>
                  </div>
                </div>

                {/* Desglose Integral de Días Libres y Demoras Navieras */}
                {expedienteSeleccionado && (
                  <IndicadorDiasLibres expediente={expedienteSeleccionado} compacto={false} />
                )}

                {/* Pase PIS Simulado con Código de Barras */}
                <div className="p-5 rounded-2xl border border-dashed border-slate-300 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.01] flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="space-y-1 text-center sm:text-left">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                      <Check className="h-3 w-3" />
                      PASE DE ACCESO PIS ACTIVO
                    </div>
                    <p className="text-sm font-black text-slate-900 dark:text-white">
                      Cita: {expedienteSeleccionado?.documentos?.pasePIS?.codigoCita || 'PIS-S/C'}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-zinc-400">
                      Transportista: {expedienteSeleccionado?.actores?.transportista?.chofer || 'Chofer Asignado'} · Placas {expedienteSeleccionado?.actores?.transportista?.placas || 'Placas S/N'}
                    </p>
                  </div>

                  {/* QR de Acceso */}
                  <div className="p-2 bg-white rounded-xl shadow-sm shrink-0">
                    <QRCodeSVG
                      value={`PIS-SSA:${expedienteSeleccionado?.documentos?.pasePIS?.codigoCita || 'PIS'}:${expedienteSeleccionado?.contenedor?.numero || 'CTR'}`}
                      size={72}
                    />
                  </div>
                </div>

                {/* Estado de la Tarja */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-white/10 space-y-1 bg-slate-50 dark:bg-white/[0.01]">
                  <p className="text-[10px] font-bold uppercase text-slate-400">Estado de la Tarja de Internación</p>
                  <p className="font-bold text-slate-900 dark:text-white">
                    {expedienteSeleccionado?.tarjaInternacion?.firmadaPorReceptor
                      ? `Firmada conforme por ${expedienteSeleccionado?.actores?.receptor?.alumnoNombre || 'Receptor'} el ${expedienteSeleccionado?.tarjaInternacion?.fechaFirma}`
                      : 'Pendiente de firma por el alumno Receptor asignado'}
                  </p>
                  {expedienteSeleccionado?.tarjaInternacion?.observacionesReceptor && (
                    <p className="text-slate-500 italic mt-1 bg-white dark:bg-[#0c0c0e] p-2 rounded-lg border border-slate-100 dark:border-white/5">
                      "{expedienteSeleccionado.tarjaInternacion.observacionesReceptor}"
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-white/10">
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => {
                        const exp = expedienteSeleccionado
                        setExpedienteSeleccionado(exp)
                        setTabActiva('simulador')
                      }}
                      className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-black text-xs uppercase tracking-wider shadow-md shadow-cyan-500/20 transition-all flex items-center justify-center gap-2"
                    >
                      <Zap className="h-4 w-4" />
                      <span>Ir a Simulación</span>
                    </button>

                    <button
                      onClick={() => {
                        setDocFisicoActivo('PEDIMENTO')
                        setModalHojaFisicaAbierto(true)
                      }}
                      className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-md shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Hojas Físicas</span>
                    </button>
                  </div>

                  <button
                    onClick={() => setExpedienteSeleccionado(null)}
                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl border border-slate-300 dark:border-white/20 text-slate-700 dark:text-zinc-200 font-bold text-xs hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========================================================================= */}
      {/* MODAL MODULACIÓN DODA Y SEMÁFORO FISCAL ANAM */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {modalModulacionDODAAbierto && (expedienteParaModular || expedienteActual) && (
          <ModalModulacionDODA
            expediente={(expedienteParaModular || expedienteActual)!}
            isOpen={modalModulacionDODAAbierto}
            onClose={() => {
              setModalModulacionDODAAbierto(false)
              setExpedienteParaModular(null)
            }}
            onCompletarModulacion={handleCompletarModulacionDODA}
            onAbrirDocumentoCompleto={(tipo) => {
              setDocFisicoActivo(tipo)
              setModalHojaFisicaAbierto(true)
            }}
          />
        )}
      </AnimatePresence>

      {/* ========================================================================= */}
      {/* MODAL 3: VISOR DE HOJA FÍSICA A PANTALLA COMPLETA */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {modalHojaFisicaAbierto && expedienteActual && (
          <motion.div
            key="modal-hoja-fisica-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setModalHojaFisicaAbierto(false)
            }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/90 backdrop-blur-md w-screen h-screen"
          >
            <motion.div
              key="modal-hoja-fisica-dialog"
              initial={{ scale: 0.97, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.97, opacity: 0, y: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl h-[94vh] max-h-[96vh] rounded-2xl bg-[#0d0e12] border border-white/10 shadow-2xl flex flex-col overflow-hidden"
            >
              <VisorExpedienteFisico
                expediente={expedienteActual}
                todosExpedientes={expedientes}
                onSeleccionarExpediente={(exp) => setExpedienteSeleccionado(exp)}
                onCerrarModal={() => setModalHojaFisicaAbierto(false)}
                documentoInicial={docFisicoActivo}
                esModal={true}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========================================================================= */}
      {/* MODAL CREAR NUEVO SALÓN O GRUPO ACADÉMICO */}
      {/* ========================================================================= */}
      <ModalCrearSalon
        isOpen={modalCrearSalonAbierto}
        onClose={() => setModalCrearSalonAbierto(false)}
        onSalonCreado={handleNuevoSalonCreado}
      />

      {/* ========================================================================= */}
      {/* MODAL VER ALUMNOS Y ROLES DEL SALÓN */}
      {/* ========================================================================= */}
      {salonParaVerAlumnos && (
        <ModalAlumnosSalon
          isOpen={Boolean(salonParaVerAlumnos)}
          onClose={() => setSalonParaVerAlumnos(null)}
          salon={salonParaVerAlumnos}
          todosMiembros={miembros}
          expedientesDelSalon={expedientes.filter(
            (e) => e.idGrupo === salonParaVerAlumnos.id_grupo || e.grupoNombre === salonParaVerAlumnos.nombre
          )}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL REASIGNAR EXPEDIENTE A OTRO SALÓN */}
      {/* ========================================================================= */}
      {expedienteParaReasignarSalon && (
        <ModalReasignarSalon
          isOpen={Boolean(expedienteParaReasignarSalon)}
          onClose={() => setExpedienteParaReasignarSalon(null)}
          expediente={expedienteParaReasignarSalon}
          grupos={grupos}
          onReasignar={handleReasignarSalon}
        />
      )}
      {/* Modal Informativo: Artículo 2 LIGIE - Reglas Generales y Complementarias */}
      <ModalReglasLigie
        abierto={mostrarModalReglasLigie}
        onCerrar={() => setMostrarModalReglasLigie(false)}
        moduloOrigen="despacho"
      />
    </div>
  )
}

export default PaginaDespachoPortuario

